/**
 * tdesign v0.21.1
 * (c) 2022 tdesign
 * @license MIT
 */

import { _ as _slicedToArray } from '../../_chunks/dep-dcf624da.mjs';
import { toRefs, computed, ref, watchEffect } from 'vue';
import { d as dayjs } from '../../_chunks/dep-f362c0f2.mjs';
import useVModel from '../../hooks/useVModel.mjs';
import { parseToDayjs, getDefaultFormat, isValidDate, formatDate, formatTime } from '../../_common/js/date-picker/format.mjs';
import { extractTimeFormat } from '../../_common/js/date-picker/utils.mjs';
import '../../_chunks/dep-556a2f2c.mjs';
import '../../_chunks/dep-7071345f.mjs';
import '../../_chunks/dep-a48de419.mjs';
import '../../_chunks/dep-baa4f9dc.mjs';
import '../../_chunks/dep-db11154e.mjs';
import '../../_chunks/dep-a82b01df.mjs';
import '../../_chunks/dep-3ca5fb38.mjs';
import '../../_chunks/dep-192e10f1.mjs';
import '../../_chunks/dep-3d4aad09.mjs';
import '../../_chunks/dep-977c5cdd.mjs';
import '../../_chunks/dep-c27b1c14.mjs';
import '../../_common/js/log/index.mjs';
import '../../_common/js/log/log.mjs';
import '../../_chunks/dep-0b4c3c44.mjs';
import '../../_chunks/dep-faa6c58f.mjs';
import '../../_chunks/dep-52cbb58a.mjs';
import '../../_chunks/dep-361522ee.mjs';
import '../../_chunks/dep-169ea640.mjs';
import '../../_chunks/dep-469bf54b.mjs';
import '../../_chunks/dep-0b4185fe.mjs';
import '../../_chunks/dep-d2403b2f.mjs';
import '../../_chunks/dep-7922050a.mjs';
import '../../_chunks/dep-59545a4d.mjs';
import '../../_chunks/dep-8e38f523.mjs';
import '../../_chunks/dep-3568c22b.mjs';
import '../../_chunks/dep-050b3eb2.mjs';

var PARTIAL_MAP = {
  first: "start",
  second: "end"
};
function initYearMonthTime(_ref) {
  var value = _ref.value,
      _ref$mode = _ref.mode,
      mode = _ref$mode === void 0 ? "date" : _ref$mode,
      format = _ref.format,
      _ref$timeFormat = _ref.timeFormat,
      timeFormat = _ref$timeFormat === void 0 ? "HH:mm:ss" : _ref$timeFormat,
      enableTimePicker = _ref.enableTimePicker;
  var defaultYearMonthTime = {
    year: [dayjs().year(), dayjs().year()],
    month: [dayjs().month(), dayjs().month()],
    time: [dayjs().format(timeFormat), dayjs().format(timeFormat)]
  };

  if (mode === "year") {
    defaultYearMonthTime.year[1] += 10;
  } else if (mode === "month" || mode === "quarter") {
    defaultYearMonthTime.year[1] += 1;
  } else if ((mode === "date" || mode === "week") && !enableTimePicker) {
    defaultYearMonthTime.month[1] += 1;
  }

  if (!value || !Array.isArray(value) || !value.length) {
    return defaultYearMonthTime;
  }

  return {
    year: value.map(function (v) {
      return parseToDayjs(v, format).year();
    }),
    month: value.map(function (v) {
      return parseToDayjs(v, format).month();
    }),
    time: value.map(function (v) {
      return parseToDayjs(v, format).format(timeFormat);
    })
  };
}
function useRangeValue(props) {
  var _toRefs = toRefs(props),
      valueFromProps = _toRefs.value,
      modelValue = _toRefs.modelValue;

  var _useVModel = useVModel(valueFromProps, modelValue, props.defaultValue, props.onChange),
      _useVModel2 = _slicedToArray(_useVModel, 2),
      value = _useVModel2[0],
      onChange = _useVModel2[1];

  var formatRef = computed(function () {
    return getDefaultFormat({
      mode: props.mode,
      format: props.format,
      valueType: props.valueType,
      enableTimePicker: props.enableTimePicker
    });
  });

  if (props.enableTimePicker) {
    if (!extractTimeFormat(formatRef.value.format)) console.error("format: ".concat(formatRef.value.format, " \u4E0D\u89C4\u8303\uFF0C\u5305\u542B\u65F6\u95F4\u9009\u62E9\u5FC5\u987B\u8981\u6709\u65F6\u95F4\u683C\u5F0F\u5316 HH:mm:ss"));
    if (!extractTimeFormat(formatRef.value.valueType) && formatRef.value.valueType !== "time-stamp") console.error("valueType: ".concat(formatRef.value.valueType, " \u4E0D\u89C4\u8303\uFF0C\u5305\u542B\u65F6\u95F4\u9009\u62E9\u5FC5\u987B\u8981\u6709\u65F6\u95F4\u683C\u5F0F\u5316 HH:mm:ss"));
  }

  if (!Array.isArray(value.value)) {
    console.error("typeof value: ".concat(value.value, " must be Array!"));
  } else if (!isValidDate(value.value, formatRef.value.valueType)) {
    console.error("value: ".concat(value.value, " is invalid dateTime! Check whether the value is consistent with format: ").concat(formatRef.value.format));
  }

  var isFirstValueSelected = ref(false);
  var time = ref(initYearMonthTime({
    value: value.value,
    mode: props.mode,
    format: formatRef.value.format,
    timeFormat: formatRef.value.timeFormat
  }).time);
  var month = ref(initYearMonthTime({
    value: value.value,
    mode: props.mode,
    format: formatRef.value.format,
    enableTimePicker: props.enableTimePicker
  }).month);
  var year = ref(initYearMonthTime({
    value: value.value,
    mode: props.mode,
    format: formatRef.value.format
  }).year);
  var cacheValue = ref(formatDate(value.value, {
    format: formatRef.value.format,
    targetFormat: formatRef.value.format
  }));
  watchEffect(function () {
    if (!value.value) {
      cacheValue.value = [];
      return;
    }

    if (!isValidDate(value.value, formatRef.value.valueType)) return;
    cacheValue.value = formatDate(value.value, {
      format: formatRef.value.format,
      targetFormat: formatRef.value.format
    });
    time.value = formatTime(value.value, formatRef.value.timeFormat);
  });
  return {
    year: year,
    month: month,
    value: value,
    time: time,
    isFirstValueSelected: isFirstValueSelected,
    cacheValue: cacheValue,
    onChange: onChange
  };
}

export { PARTIAL_MAP, useRangeValue as default, initYearMonthTime };
//# sourceMappingURL=useRangeValue.mjs.map
